import React from "react";
import { Link } from "react-router-dom";
import useReveal from "@/hooks/useReveal";
import { useI18n } from "@/context/LangContext";
import { Factory, Cube, Receipt, UsersThree, Warehouse, ChartBar, Handshake, ShieldCheck, Timer, ArrowRight } from "@phosphor-icons/react";

const MODULES = [
  { icon: Factory, key: "erp.mod.mfg" },
  { icon: Warehouse, key: "erp.mod.inv" },
  { icon: Receipt, key: "erp.mod.fin" },
  { icon: UsersThree, key: "erp.mod.hr" },
  { icon: Cube, key: "erp.mod.ps" },
  { icon: ChartBar, key: "erp.mod.bi" },
];

const PROOF = [
  { icon: Timer, k: "30 days", vKey: "erp.proof.golive" },
  { icon: ShieldCheck, k: "UAE · EU · IN", vKey: "erp.proof.region" },
  { icon: Handshake, k: "42 clients", vKey: "erp.proof.clients" },
];

export default function ErpLeadership() {
  const ref = useReveal();
  const { t } = useI18n();
  return (
    <section
      id="erp"
      ref={ref}
      data-testid="erp-leadership"
      className="reveal section border-b border-[color:var(--line)] bg-white relative overflow-hidden"
    >
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-[color:var(--brand)] opacity-[0.06] blur-3xl pointer-events-none" aria-hidden />
      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="w-6 h-px bg-[color:var(--brand)]" />
              <div className="eyebrow">{t("erp.eyebrow")}</div>
            </div>
            <h2 className="font-display font-black text-4xl md:text-5xl mt-4 leading-[1.02] tracking-tight">
              {t("erp.title")}
            </h2>
            <p className="mt-6 text-neutral-700 leading-relaxed max-w-lg">
              {t("erp.sub")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/#products" data-testid="erp-cta-products" className="btn-brand text-sm">
                {t("erp.seeProducts")} <ArrowRight size={14} weight="bold" />
              </Link>
              <Link to="/#contact" data-testid="erp-cta-quote" className="btn-ghost text-sm">{t("erp.proposal")}</Link>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 md:grid-cols-3 border-l border-t border-[color:var(--line)]">
              {MODULES.map((m) => {
                const I = m.icon;
                return (
                  <div
                    key={m.key}
                    data-testid={`erp-mod-${m.key.split(".").pop()}`}
                    className="border-r border-b border-[color:var(--line)] p-5 flex items-start gap-3 hover:bg-[color:var(--paper-alt)] transition-colors"
                  >
                    <div className="shrink-0 w-9 h-9 border border-[color:var(--ink)] flex items-center justify-center">
                      <I size={18} weight="bold" />
                    </div>
                    <div className="font-display font-semibold text-sm md:text-base leading-snug">
                      {t(m.key)}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              {PROOF.map((p) => {
                const I = p.icon;
                return (
                  <div key={p.k} className="border border-[color:var(--line)] p-4 bg-[color:var(--paper-alt)]">
                    <I size={18} weight="bold" className="text-[color:var(--brand)]" />
                    <div className="mt-2 font-display font-black text-lg leading-tight">{p.k}</div>
                    <div className="mt-1 text-xs text-neutral-600 leading-relaxed">
                      {t(p.vKey)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
